import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MultiSelectDropdown, { Option } from "@tKgpco/Components/MultiSelectDropdown";
import purchaseHistoryReportHandler from "@tKgpco/Handlers/CustomPurchaseHistoryhandler";
import { getDiscrepancyReport } from "@tKgpco/Services/DiscrepancyService";
import { getRMAReport } from "@tKgpco/Services/RMAService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import vendorsHandler from "@tKgpco/Store/Data/Vendors/Handlers/VendorsHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    closePopup: () => void;
    configurationModalIsOpen: boolean;
    reportFormat: string;
    reportType: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasShellContext;

const mapStateToProps = (state: CustomApplicationState) => {
    const session = getSession(state);
    return {
        billTo: getBillToState(state, session.billToId).value,
        vendorsState: state.KGPState.vendorsState,
    };
};

const mapDispatchToProps = {
    vendorsHandler,
    purchaseHistoryReportHandler,
};

export interface CurrentShippingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    reportConfigurationModal?: ModalPresentationProps;
    radioButtonStyles?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioOption?: FieldSetPresentationProps<RadioComponentProps>;
    gridItem?: GridItemProps;
    fieldsContainer?: GridItemProps;
    dateGridItem?: GridItemProps;
    datePicker?: DatePickerPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    vendorDropdown?: InjectableCss;
    fieldsGridItem?: GridItemProps;
    clearButton?: ButtonPresentationProps;
}

export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
    gridContainer: {
        gap: 5,
        css: css`
            border-radius: 4px;
            padding: 10px;
            border: 2px solid #f1f6fb;
        `,
    },
    fieldsGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0px;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;

            &.cta-container {
                flex-direction: row;
                gap: 10px;
            }
        `,
    },
    dateGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    fieldsContainer: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    headingGridItem: { width: 10 },
    headingText: {
        css: css`
            font-size: 1rem;
            font-weight: 600;
        `,
    },
    datePicker: {},

    reportConfigurationModal: { sizeVariant: "medium" },

    centeringWrapper: {
        css: css`
            height: 100px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    vendorDropdown: {
        css: css`
            min-width: 200px !important;
        `,
    },
    clearButton: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            float: right;
            text-transform: uppercase;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            & :hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
        `,
    },
};

const styles = currentShippingAddressStyles;

const radioOptionsData = [
    {
        value: "manufacturer",
        description: "View by Manufacturer (Includes all Items)",
    },
    {
        value: "item",
        description: "View by Item # (Single Item Only)",
    },
    {
        value: "customer",
        description: "View by Customer Alias (Single Item Only)",
    },
];

const ReportConfigurationModal: React.FunctionComponent<Props> = ({
    closePopup,
    configurationModalIsOpen,
    vendorsHandler,
    vendorsState,
    reportFormat,
    purchaseHistoryReportHandler,
    billTo,
    reportType,
    shellContext,
}: Props) => {
    const [searchType, setSearchType] = useState("manufacturer");
    const [searchValue, setSearchValue] = useState("");
    const [fromDate, setFromDate] = useState<Date | undefined>(new Date());
    const [toDate, setToDate] = useState<Date | undefined>(new Date());
    const toasterContext = React.useContext(ToasterContext);
    const [selectedVendors, setSelectedVendors] = useState<Option[]>([]);

    const [rmaNumber, setRmaNumber] = useState("");
    const [discrepancyId, setDiscrepancyId] = useState("");
    const [poNumber, setPoNumber] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");

    const [customerPartNumber, setCustomerPartNumber] = useState("");
    const [itemNumber, setItemNumber] = useState("");
    const [supplierName, setSupplierName] = useState("");
    const [businessCase, setBusinessCase] = useState("1");

    const history = useHistory();

    useEffect(() => {
        getVendors();
        setFromDate(new Date());
        setToDate(new Date());
    }, [configurationModalIsOpen]);

    useEffect(() => {
        setSearchValue("");
    }, [searchType]);

    const getVendors = () => {
        if (configurationModalIsOpen && !vendorsState.vendorsFetched && reportType === "purchaseHistory") {
            vendorsHandler({
                onError: message => {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                },
            });
        }
    };

    const validateForm = () => {
        if (reportType === "rmaReports") {
            return !!(fromDate || toDate || rmaNumber || poNumber || selectedStatus);
        }

        if (reportType === "discrepancyReports") {
            return !!(fromDate || toDate || discrepancyId || poNumber || selectedStatus);
        }
        if (reportType === "itemTrackingReports") {
            return !!(fromDate || toDate || itemNumber || supplierName || customerPartNumber || businessCase);
        }

        let isValid = false;
        if (searchType === "item" && searchValue) {
            isValid = true;
        } else if (searchType === "customer" && searchValue) {
            isValid = true;
        } else if (searchType === "manufacturer" && selectedVendors.length) {
            isValid = true;
        }
        return !!(searchType && isValid && fromDate && toDate);
    };

    const isValid = validateForm();

    const modalCloseHandler = (e: any) => {
        e.preventDefault();
        clearForm();
        closePopup();
    };

    const radioButtonChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchType(event.target.value);
    };

    const changeDateFormatToString = (date: Date | undefined) => {
        if (!date) {
            return;
        }
        const yyyy = date.getFullYear();
        let mm: string | number = date.getMonth() + 1;
        let dd: string | number = date.getDate();

        if (dd < 10) {
            dd = `0${dd}`;
        }
        if (mm < 10) {
            mm = `0${mm}`;
        }

        return `${yyyy}/${mm}/${dd}`;
    };

    const startDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setFromDate(selectedDay || new Date());
        if (selectedDay && toDate && selectedDay > toDate) {
            setToDate(undefined);
        }
    };

    const toDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setToDate(selectedDay);
    };

    const getReport = () => {
        // const isValid = validateForm();
        let isValid = false;
        if (searchType === "item" && searchValue) {
            isValid = true;
        } else if (searchType === "customer" && searchValue) {
            isValid = true;
        } else if (searchType === "manufacturer" && selectedVendors.length) {
            isValid = true;
        }
        if (searchType && isValid && fromDate && toDate) {
            const formData = {
                searchType,
                searchValue:
                    searchType === "manufacturer" ? selectedVendors.map(option => option.value).join("|") : searchValue,
                fromDate: changeDateFormatToString(fromDate),
                toDate: changeDateFormatToString(toDate),
                outputformat: reportFormat,
            };

            purchaseHistoryReportHandler({
                formData,
                onSuccess: () => {
                    closePopup();
                    clearForm();
                    setSelectedVendors([]);
                    toasterContext.addToast({ body: siteMessage("Report_SendMail"), messageType: "success" });
                },
                onError: message => {
                    toasterContext.addToast({ body: message as any, messageType: "danger" });
                },
            });
        }
    };

    const getVersionReport = () => {
        if (reportType === "itemTrackingReports" && typeof window !== "undefined") {
            const url =
                "/MyAccount/Reports/ItemTracking?fromDate=" +
                (fromDate ? changeDateFormatToString(fromDate) : "") +
                "&toDate=" +
                (toDate ? changeDateFormatToString(toDate) : "") +
                "&customerPartNumber=" +
                customerPartNumber +
                "&itemId=" +
                itemNumber +
                "&supplierName=" +
                supplierName +
                "&businessCaseId=" +
                businessCase +
                "&page=1&isExport=false";

            if (!shellContext.isInShell) {
                closePopup();
                window.open(url, "_blank");
            } else {
                history.push(url);
                closePopup();
            }

            return;
        }

        if (reportType === "rmaReports") {
            getRMAReport({
                rmaNumber,
                poNumber,
                fromDate: changeDateFormatToString(fromDate),
                toDate: changeDateFormatToString(toDate),
                status: selectedStatus,
            })
                .then(() => {
                    toasterContext.addToast({ body: siteMessage("RMA_Report_Success"), messageType: "success" });
                })
                .catch(() => {
                    toasterContext.addToast({ body: siteMessage("RMA_Report_Error"), messageType: "danger" });
                })
                .finally(() => {
                    clearForm();
                    closePopup();
                });
        } else {
            getDiscrepancyReport({
                discrepancyId,
                poNumber,
                fromDate: changeDateFormatToString(fromDate),
                toDate: changeDateFormatToString(toDate),
                status: selectedStatus,
            })
                .then(() => {
                    toasterContext.addToast({
                        body: siteMessage("Discrepancy_Report_Success"),
                        messageType: "success",
                    });
                })
                .catch(() => {
                    toasterContext.addToast({ body: siteMessage("Discrepancy_Report_Error"), messageType: "danger" });
                })
                .finally(() => {
                    clearForm();
                    closePopup();
                });
        }
    };

    const clearForm = () => {
        setSearchType("manufacturer");
        setSearchValue("");
        setFromDate(undefined);
        setToDate(undefined);
        setSelectedVendors([]);
        setRmaNumber("");
        setDiscrepancyId("");
        setPoNumber("");
        setSelectedStatus("");
        setCustomerPartNumber("");
        setItemNumber("");
        setSupplierName("");
        setBusinessCase("1");
    };

    const handleDropdownChange = (option: Option) => {
        const isSelected =
            selectedVendors &&
            selectedVendors.length > 0 &&
            selectedVendors.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedVendors && selectedVendors.length > 0 ? [...selectedVendors] : [];
        if (isSelected) {
            status = selectedVendors!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedVendors(status);
        } else {
            setSelectedVendors([]);
        }
    };

    const options = vendorsState.vendors || [];
    const uniqueOptions = new Map<string, string[]>();
    for (const option of options) {
        const accessorialsOption = uniqueOptions.get(option.name) || [];
        accessorialsOption.push(option.id);
        uniqueOptions.set(option.name, accessorialsOption);
    }

    const dropDownOptions: Option[] = Array.from(uniqueOptions, ([name, values]) => ({ name, values }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptions: Option[] =
        selectedVendors && selectedVendors.length > 0
            ? selectedVendors.map(val => ({
                  value: val.value,
                  label: dropDownOptions.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];
    return (
        <Modal
            headline={
                reportType === "rmaReports"
                    ? translate("Configure RMA Report")
                    : reportType === "itemTrackingReports"
                      ? translate("Configure Item Tracking Report")
                      : reportType === "discrepancyReports"
                        ? translate("Configure Discrepancy Report")
                        : translate("Configure Purchase History Report")
            }
            {...styles.reportConfigurationModal}
            isOpen={configurationModalIsOpen}
            handleClose={modalCloseHandler}
        >
            <StyledWrapper>
                <GridContainer>
                    <GridItem {...styles.gridItem}>
                        {reportType === "itemTrackingReports" ? (
                            <Typography as="div">
                                <Select
                                    value={businessCase}
                                    label={translate("Business Case")}
                                    onChange={e => setBusinessCase(e.target.value)}
                                    css={css`
                                        width: 200px;
                                    `}
                                >
                                    <option value="1">{translate("Verizon")}</option>
                                    <option value="7">{translate("Vartac")}</option>
                                </Select>
                            </Typography>
                        ) : (
                            <Typography as="div">
                                <strong>Customer Number: </strong>
                                <Typography as="span">{billTo?.customerNumber}</Typography>
                            </Typography>
                        )}
                    </GridItem>
                    <GridItem {...styles.dateGridItem}>
                        <DatePicker
                            {...styles.datePicker}
                            label={translate("Start Date")}
                            selectedDay={fromDate}
                            onDayChange={startDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                maxDate: new Date(),
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.dateGridItem}>
                        <DatePicker
                            {...styles.datePicker}
                            label={translate("To Date")}
                            selectedDay={toDate}
                            onDayChange={toDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                minDate: fromDate,
                            }}
                        />
                    </GridItem>
                    {reportType === "itemTrackingReports" && (
                        <GridItem {...styles.fieldsGridItem}>
                            <GridItem {...styles.fieldsContainer}>
                                <TextField
                                    label={translate("Customer Part #")}
                                    value={customerPartNumber}
                                    onChange={e => setCustomerPartNumber(e.target.value)}
                                />
                            </GridItem>
                            <GridItem {...styles.fieldsContainer}>
                                <TextField
                                    label={translate("Item #")}
                                    value={itemNumber}
                                    onChange={e => setItemNumber(e.target.value)}
                                />
                            </GridItem>
                            <GridItem {...styles.fieldsContainer}>
                                <TextField
                                    label={translate("Supplier Name ")}
                                    value={supplierName}
                                    onChange={e => setSupplierName(e.target.value)}
                                />
                            </GridItem>
                        </GridItem>
                    )}
                    {(reportType === "rmaReports" || reportType === "discrepancyReports") && (
                        <GridItem {...styles.fieldsGridItem}>
                            <GridItem {...styles.fieldsContainer}>
                                {reportType === "rmaReports" ? (
                                    <TextField
                                        label={translate("RMA #")}
                                        value={rmaNumber}
                                        onChange={e => setRmaNumber(e.target.value)}
                                    />
                                ) : (
                                    <TextField
                                        label={translate("Discrepancy #")}
                                        value={discrepancyId}
                                        onChange={e => setDiscrepancyId(e.target.value)}
                                    />
                                )}
                            </GridItem>
                            <GridItem {...styles.fieldsContainer}>
                                <TextField
                                    label={translate("PO #")}
                                    value={poNumber}
                                    onChange={e => setPoNumber(e.target.value)}
                                />
                            </GridItem>
                            <GridItem {...styles.fieldsContainer}>
                                <Select
                                    value={selectedStatus}
                                    label={translate("Status")}
                                    onChange={e => setSelectedStatus(e.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    <option value="O">{translate("Open")}</option>
                                    <option value="I">{translate("In Progress")}</option>
                                    <option value="P">{translate("Pending")}</option>
                                    <option value="C">{translate("Complete")}</option>
                                </Select>
                            </GridItem>
                        </GridItem>
                    )}
                    {reportType === "purchaseHistory" && (
                        <>
                            <GridItem {...styles.gridItem}>
                                <RadioGroup
                                    value={searchType}
                                    onChangeHandler={radioButtonChangeHandler}
                                    label="Select one of the following options for your Purchase History Report"
                                    {...styles.radioButtonStyles}
                                >
                                    {radioOptionsData.map(option => {
                                        return (
                                            <Radio key={option.value} value={option.value} {...styles.radioOption}>
                                                {translate(option.description)}
                                            </Radio>
                                        );
                                    })}
                                </RadioGroup>
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                {searchType === "manufacturer" && vendorsState.isLoading ? (
                                    <StyledWrapper {...styles.centeringWrapper}>
                                        <LoadingSpinner {...styles.spinner} />
                                    </StyledWrapper>
                                ) : searchType === "manufacturer" ? (
                                    <>
                                        <label>
                                            {`Listed below are the suppliers you made purchases from during [${
                                                fromDate && fromDate.toLocaleDateString()
                                            } - ${toDate && toDate.toLocaleDateString()}]. Select one or
                                    more Suppliers to include on the report. Hold down the "Ctrl" key to select multiple
                                    rows. :`}
                                        </label>

                                        <MultiSelectDropdown
                                            label=""
                                            handleOptionClick={handleDropdownChange}
                                            options={dropDownOptions}
                                            selectedOptions={selectedOptions}
                                            placeHolder={translate("Please select the vendor")}
                                        />
                                    </>
                                ) : null}
                                {searchType === "item" && (
                                    <StyledWrapper>
                                        <TextField
                                            label="Enter an Item # to request the report:"
                                            onChange={e => setSearchValue(e.target.value)}
                                        ></TextField>
                                    </StyledWrapper>
                                )}
                                {searchType === "customer" && (
                                    <StyledWrapper>
                                        <TextField
                                            label="Enter a Customer Alias to request the report:"
                                            onChange={e => setSearchValue(e.target.value)}
                                        ></TextField>
                                    </StyledWrapper>
                                )}
                            </GridItem>{" "}
                        </>
                    )}
                    <GridItem {...styles.gridItem} className="cta-container">
                        <Button
                            disabled={!isValid}
                            onClick={reportType === "purchaseHistory" ? getReport : getVersionReport}
                        >
                            {translate("Run Report")}
                        </Button>
                        {(reportType === "rmaReports" ||
                            reportType === "discrepancyReports" ||
                            reportType === "itemTrackingReports") && (
                            <Button {...styles.clearButton} onClick={clearForm}>
                                {translate("Clear")}
                            </Button>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(ReportConfigurationModal));
